import React, { useState } from "react";
import { useLanguage } from "@/App";
import { 
  Globe, 
  ShoppingCart, 
  Palette, 
  Bot, 
  Sparkles, 
  MessageSquare, 
  Code, 
  Server, 
  Terminal,
  ArrowRight
} from "lucide-react";

const Services = () => {
  const { t, language } = useLanguage();
  const [activeCategory, setActiveCategory] = useState(0);

  const categories = t("services.categories");

  const iconMap = {
    "Custom Web Design": <Globe size={28} />,
    "Custom Webdesign": <Globe size={28} />,
    "E-Commerce Solutions": <ShoppingCart size={28} />,
    "E-Commerce Oplossingen": <ShoppingCart size={28} />,
    "UI/UX Design": <Palette size={28} />,
    "AI Avatar Creator": <Bot size={28} />,
    "AI Automation": <Sparkles size={28} />,
    "AI Automatisering": <Sparkles size={28} />,
    "AI Chatbots": <MessageSquare size={28} />,
    "Custom Software": <Code size={28} />,
    "API Development": <Server size={28} />,
    "API Ontwikkeling": <Server size={28} />,
    "Code Solutions": <Terminal size={28} />,
    "Code Oplossingen": <Terminal size={28} />
  };

  const categoryColors = [
    "from-[#FF6B00]/20 to-transparent",
    "from-purple-500/20 to-transparent", 
    "from-blue-500/20 to-transparent"
  ];

  const categoryAccents = [
    "text-[#FF6B00]",
    "text-purple-400",
    "text-blue-400"
  ];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#121212] via-transparent to-transparent opacity-50" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <div className="mb-16">
          <p
            data-testid="services-overline"
            className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4"
          >
            {t("services.overline")}
          </p>
          <h2
            data-testid="services-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {t("services.title")}
          </h2>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-12">
          {categories.map((category, index) => (
            <button
              key={index}
              onClick={() => setActiveCategory(index)}
              data-testid={`service-category-${index}`}
              className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-all ${
                activeCategory === index
                  ? `bg-white text-black`
                  : "bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white"
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
          {categories[activeCategory].items.map((service, index) => (
            <div
              key={index}
              data-testid={`service-card-${index}`}
              className={`relative bg-[#0A0A0A] p-8 md:p-10 group hover:bg-[#121212] transition-all duration-500 overflow-hidden`}
            >
              {/* Gradient overlay on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${categoryColors[activeCategory]} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              
              <div className="relative z-10">
                <div className={`${categoryAccents[activeCategory]} mb-6 transform group-hover:scale-110 transition-transform duration-300`}>
                  {iconMap[service.title] || <Code size={28} />}
                </div>
                <h3
                  className="text-xl font-bold text-white mb-3 group-hover:text-white transition-colors"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {service.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed mb-6">
                  {service.description}
                </p>
                <button 
                  className={`inline-flex items-center gap-2 text-sm font-bold ${categoryAccents[activeCategory]} opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0`}
                >
                  {language === "nl" ? "Meer info" : "Learn more"} <ArrowRight size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-zinc-400 mb-6">
            {language === "nl" ? "Iets speciaals nodig? Wij regelen het." : "Need something custom? We've got you covered."}
          </p>
          <a
            href="#contact"
            data-testid="services-cta"
            className="inline-flex items-center gap-3 bg-[#FF6B00] text-white px-8 py-4 text-sm font-bold tracking-wide hover:bg-[#FF6B00]/90 transition-all"
          >
            {language === "nl" ? "Bespreek Je Project" : "Discuss Your Project"}
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;
